<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { messageStamp } from 'shared/helpers/timeHelper';
import format from 'date-fns/format';
import DatePicker from 'vue-datepicker-next';

import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import Input from 'next/input/Input.vue';
import Dropdown from 'next/wgpt/Dropdown/Dropdown.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import PaginationFooter from 'next/pagination/PaginationFooter.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  botId: { type: Number, required: true },
  toolSets: { type: Array, default: () => [] },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const currentPage = ref(1);
const expandedId = ref(null);
const dateFrom = ref(null);
const dateTo = ref(null);
const selectedToolSetId = ref(null);

const hasDateFilter = computed(() => dateFrom.value || dateTo.value);
const hasAnyFilter = computed(
  () => hasDateFilter.value || selectedToolSetId.value
);

const selectedToolSetName = computed(() => {
  if (!selectedToolSetId.value) return '';
  const ts = props.toolSets.find(item => item.id === selectedToolSetId.value);
  return ts?.name || '';
});

const toolSetMenuItems = computed(() =>
  props.toolSets.map(ts => ({
    label: ts.name,
    value: ts.id,
    action: 'select',
    isSelected: selectedToolSetId.value === ts.id,
  }))
);

const formatDateForApi = date => {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

const formatDateDisplay = date => {
  if (!date) return '';
  return format(date, 'LLL d, y h:mm aa');
};

const onFromChange = value => {
  dateFrom.value = value || null;
};

const onToChange = value => {
  dateTo.value = value || null;
};

const clearFilters = () => {
  dateFrom.value = null;
  dateTo.value = null;
  selectedToolSetId.value = null;
};

const onToolSetSelect = (item, toggle) => {
  selectedToolSetId.value =
    selectedToolSetId.value === item.value ? null : item.value;
  toggle();
};

const toggleExpanded = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const formatPayload = payload => {
  if (!payload) return '—';
  try {
    const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(payload);
  }
};

const records = useMapGetter('wgptBotTools/getExecutions');
const meta = useMapGetter('wgptBotTools/getExecutionsMeta');
const uiFlags = useMapGetter('wgptBotTools/getExecutionsUIFlags');

const isFetching = computed(() => uiFlags.value.isFetching);
const totalItems = computed(() => meta.value.total || 0);
const itemsPerPage = computed(() => meta.value.size || 15);

const fetchExecutions = () => {
  store.dispatch('wgptBotTools/getExecutions', {
    botId: props.botId,
    page: currentPage.value,
    toolSetId: selectedToolSetId.value || undefined,
    from: formatDateForApi(dateFrom.value),
    to: formatDateForApi(dateTo.value),
  });
};

watch([dateFrom, dateTo, selectedToolSetId], () => {
  currentPage.value = 1;
  expandedId.value = null;
  fetchExecutions();
});

const formatDate = date =>
  messageStamp(new Date(date).getTime() / 1000, 'LLL d, y, h:mm:ss aa');

const onPageChange = page => {
  currentPage.value = page;
  fetchExecutions();
};

const open = () => {
  currentPage.value = 1;
  expandedId.value = null;
  dateFrom.value = null;
  dateTo.value = null;
  selectedToolSetId.value = null;
  store.dispatch('wgptBotTools/clearExecutions');
  dialogRef.value?.open();
  fetchExecutions();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="3xl"
    position="top"
    overflow-y-auto
    custom-dialog-class="!mb-8 max-h-[calc(100vh-8rem)]"
  >
    <div class="flex items-center justify-between mb-2">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium leading-6 text-n-slate-12">
          {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TITLE') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-x"
        variant="ghost"
        color="slate"
        size="sm"
        @click="close"
      />
    </div>

    <div class="flex items-center gap-3 mb-4">
      <Dropdown align="start">
        <template #trigger="{ toggle }">
          <div
            class="flex items-center relative cursor-pointer"
            @click="toggle"
          >
            <Input
              :model-value="selectedToolSetName"
              :placeholder="
                t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.FILTER.TOOL_SET')
              "
              custom-input-class="!h-8 cursor-pointer"
              type="text"
              readonly
              size="sm"
              class="w-40 [&_input]:cursor-pointer [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-filter"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 size-3.5 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
        </template>
        <template #default="{ toggle }">
          <DropdownMenu
            class="!static"
            :menu-items="toolSetMenuItems"
            @action="item => onToolSetSelect(item, toggle)"
          />
        </template>
      </Dropdown>

      <Dropdown align="start">
        <template #trigger="{ toggle }">
          <div
            class="flex items-center relative cursor-pointer"
            @click="toggle"
          >
            <Input
              :model-value="dateFrom ? formatDateDisplay(dateFrom) : ''"
              :placeholder="
                t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.FILTER.FROM')
              "
              custom-input-class="!h-8 cursor-pointer"
              type="text"
              readonly
              size="sm"
              class="w-44 [&_input]:cursor-pointer [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-calendar"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 size-3.5 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
        </template>
        <template #default="{ toggle }">
          <DatePicker
            class="execution-log-date-picker"
            type="datetime"
            confirm
            :clearable="false"
            :editable="false"
            open
            :confirm-text="t('DIALOG.BUTTONS.CONFIRM')"
            :value="dateFrom"
            :append-to-body="false"
            @change="onFromChange"
            @confirm="toggle"
          />
        </template>
      </Dropdown>

      <Dropdown align="start">
        <template #trigger="{ toggle }">
          <div
            class="flex items-center relative cursor-pointer"
            @click="toggle"
          >
            <Input
              :model-value="dateTo ? formatDateDisplay(dateTo) : ''"
              :placeholder="
                t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.FILTER.TO')
              "
              custom-input-class="!h-8 cursor-pointer"
              type="text"
              readonly
              size="sm"
              class="w-44 [&_input]:cursor-pointer [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-calendar"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 size-3.5 ltr:left-2.5 rtl:right-2.5"
                />
              </template>
            </Input>
          </div>
        </template>
        <template #default="{ toggle }">
          <DatePicker
            class="execution-log-date-picker"
            type="datetime"
            confirm
            :clearable="false"
            :editable="false"
            open
            :confirm-text="t('DIALOG.BUTTONS.CONFIRM')"
            :value="dateTo"
            :append-to-body="false"
            @change="onToChange"
            @confirm="toggle"
          />
        </template>
      </Dropdown>

      <Button
        v-if="hasAnyFilter"
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.FILTER.CLEAR')"
        size="xs"
        variant="ghost"
        color="slate"
        @click="clearFilters"
      />
    </div>

    <div class="flex flex-col -mx-2 min-h-[300px]">
      <div
        v-if="isFetching && !records.length"
        class="flex items-center justify-center py-16"
      >
        <SpinnerLoader :size="24" class="text-n-blue-9" />
      </div>

      <template v-else-if="records.length">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-n-slate-11">
              <th class="px-3 py-2.5 text-start font-medium">
                {{
                  t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.TOOL_NAME')
                }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{
                  t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.DURATION')
                }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{
                  t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.EXECUTED_AT')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="execution in records" :key="execution.id">
              <tr
                class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-1 transition-colors"
                :class="{ 'bg-n-alpha-1': expandedId === execution.id }"
                @click="toggleExpanded(execution.id)"
              >
                <td class="px-3 py-2.5 text-n-slate-12 font-medium">
                  <div class="flex items-center gap-2">
                    <Icon
                      icon="i-lucide-chevron-right"
                      class="size-3.5 text-n-slate-10 transition-transform duration-200 flex-shrink-0"
                      :class="{
                        'rotate-90': expandedId === execution.id,
                      }"
                    />
                    {{ execution.tool_name }}
                  </div>
                </td>
                <td class="px-3 py-2.5">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                    :class="
                      execution.status === 'success'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-ruby-3 text-n-ruby-11'
                    "
                  >
                    {{
                      execution.status === 'success'
                        ? t(
                            'WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.STATUS.SUCCESS'
                          )
                        : t(
                            'WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.STATUS.ERROR'
                          )
                    }}
                  </span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 tabular-nums">
                  {{
                    t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.DURATION_MS', {
                      duration: execution.duration_ms,
                    })
                  }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-11">
                  {{ formatDate(execution.created_at) }}
                </td>
              </tr>
              <tr
                v-if="expandedId === execution.id"
                class="border-b border-n-weak"
              >
                <td colspan="4" class="px-3 py-3">
                  <div class="flex gap-3">
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                        {{
                          t(
                            'WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.REQUEST'
                          )
                        }}
                      </p>
                      <pre
                        class="text-xs text-n-slate-12 bg-n-alpha-2 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words"
                        >{{ formatPayload(execution.request_payload) }}</pre
                      >
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                        {{
                          t(
                            'WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TABLE.RESPONSE'
                          )
                        }}
                      </p>
                      <pre
                        class="text-xs text-n-slate-12 bg-n-alpha-2 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words"
                        >{{ formatPayload(execution.response_payload) }}</pre
                      >
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="isFetching" class="flex items-center justify-center py-4">
          <SpinnerLoader :size="16" class="text-n-blue-9" />
        </div>
      </template>

      <div v-else class="flex flex-col items-center justify-center py-16 gap-3">
        <Icon icon="i-lucide-scroll-text" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.EMPTY') }}
        </p>
      </div>
    </div>

    <template v-if="totalItems > itemsPerPage" #footer>
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        @update:current-page="onPageChange"
      />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.execution-log-date-picker {
  @apply w-[248px];
  ::v-deep {
    .mx-calendar-panel-date {
      @apply px-3 py-1;
    }
    .mx-datepicker-footer {
      @apply border-t-0 p-3 pt-0 pb-2;
    }
    .mx-input-wrapper {
      @apply hidden;
    }
    .mx-datepicker-popup {
      @apply bg-n-solid-2 shadow-none rounded-xl;
      position: static !important;
    }
    .mx-time {
      @apply bg-n-solid-2 shadow-none rounded-xl;
    }
    .mx-time-column {
      @apply border-n-weak;
    }
    .mx-datepicker-btn-confirm {
      @apply bg-n-brand text-white hover:brightness-110 outline-transparent;
      @apply h-8 px-3;
      @apply text-sm;
      @apply inline-flex items-center justify-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50;
    }
  }
}
</style>
